import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineUsergroupAdd } from 'react-icons/ai'
import { BsInfoCircleFill } from 'react-icons/bs'
import { FaUserEdit } from 'react-icons/fa'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors, IKReactSelect } from '../../../ui-kit'
import { FeaturesEnum } from '../../../utils/features'
import { isRestrictedUser } from '../../../utils/roles'
import useToast from '../../hooks/useToast'
import type {
  AddUserResponse,
  UserErrorResponse,
  UserSessionResponse,
  UsersResponse,
} from '../../interfaces'

interface Props {
  isUpdate: boolean
  label?: string
  userIds?: string[]
  id?: string
  user: UserSessionResponse | null
  isOpen: boolean
  onClose(): void
  getData(): Promise<void>
}

interface FormProps {
  label: string
  userField: {
    label: string
    value: string
  }[]
}

const CreateUpdateUserGroupModal = ({
  isUpdate,
  label,
  userIds,
  id,
  user,
  isOpen,
  onClose,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [initialValues, setInitialValues] = useState<{
    label: string
    userField: {
      label: string
      value: string
    }[]
  }>({ label: '', userField: [] })
  const [formError, setFormError] = useState<string>('')
  const [userOptions, setUserOptions] = useState<
    Array<{
      label: string
      value: string
    }>
  >([])
  const [users, setUsers] = useState<
    Array<{
      label: string
      value: string
    }>
  >([])

  useEffect(() => {
    initializeForm()
  }, [isOpen])

  const initializeForm = async (): Promise<void> => {
    if (
      user?.clientNumber &&
      user?.pricingPlan.featuresList.filter(feature =>
        [
          FeaturesEnum.USER_MANAGEMENT,
          FeaturesEnum.ACL_AND_COLLECTIONS,
        ].includes(feature as FeaturesEnum),
      ).length === 2
    ) {
      const response = (await request.get(
        `/api/clients/${user.clientNumber}/users`,
      )) as UsersResponse[]

      if (request.error || !response) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        const userEmailAndIdOptions = response
          .filter(user => isRestrictedUser(user?.roles))
          .map(user => {
            return { label: user.userEmail, value: user._id }
          })
        setUserOptions(userEmailAndIdOptions)
        setUsers([])

        if (isUpdate) {
          const userEmailandIdsInGroup = response
            .filter(
              user =>
                userIds?.includes(user._id) && isRestrictedUser(user?.roles),
            )
            .map(user => {
              return { label: user.userEmail, value: user._id }
            })

          setInitialValues({
            label: label ?? '',
            userField: userEmailandIdsInGroup,
          })
          setUsers(userEmailandIdsInGroup)
        }
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber && isUpdate) {
              const response = (await request.put(
                `/api/clients/${user.clientNumber}/groups/${id}`,
                {
                  label: values.label,
                  userIds: values.userField.map(user => user.value),
                },
              )) as null | UserErrorResponse

              if (request.error) {
                setFormError(
                  response?.message ??
                    t('something_went_wrong', 'Something went wrong'),
                )
                return
              } else {
                toast({
                  description: t(
                    'successfully_updated',
                    'Successfully updated',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })

                await getData()
              }

              onClose()
            } else if (user?.clientNumber) {
              const response = (await request.post(
                `/api/clients/${user.clientNumber}/groups`,
                {
                  label: values.label,
                  userIds: values.userField.map(user => user.value),
                },
              )) as UserErrorResponse | AddUserResponse

              if (request.error) {
                setFormError(
                  (response as UserErrorResponse).message ??
                    t('something_went_wrong', 'Something went wrong'),
                )
                return
              } else {
                toast({
                  description: t('successfully_added', 'Successfully added'),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                setFormError('')
                onClose()
                await getData()
              }
            }
          }}
          validationSchema={Yup.object().shape({
            label: Yup.string()
              .required('Required')
              .matches(
                /^[a-zA-Z0-9 ]+$/u,
                'Group name can only have alphanumeric characters',
              ),
          })}
        >
          {({ isSubmitting, dirty, submitForm, setFieldValue }) => (
            <>
              <ModalHeader>
                <Icon
                  as={isUpdate ? FaUserEdit : AiOutlineUsergroupAdd}
                  color={colors.appBattleshipGrey}
                  backgroundColor={colors.appPaleGrey}
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="50%"
                  marginRight="0.5em"
                />
                {isUpdate
                  ? t('update_user_group', 'Update an existing user group')
                  : t('create_new_user_group', 'Create a new user group')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="label">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isInvalid={Boolean(
                        form.errors.label && form.touched.label,
                      )}
                      isRequired
                    >
                      <FormLabel>{t('name', 'Name')}</FormLabel>
                      <Input
                        {...field}
                        placeholder={t('group_name', 'Group name')}
                      />
                      {form.errors.label && form.touched.label ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.label}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="userField">
                  {({ form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(form.errors.userField)}
                    >
                      <HStack spacing={0}>
                        <FormLabel marginRight={0}>
                          {t('users', 'Users')}
                        </FormLabel>
                        <Popover trigger="hover">
                          <PopoverTrigger>
                            <Flex>
                              <IconButton
                                aria-label="Info icon"
                                icon={<BsInfoCircleFill />}
                                color={colors.appBlueyGrey}
                                size="sm"
                                variant="link"
                                marginBottom={2}
                                marginLeft={0}
                              />
                            </Flex>
                          </PopoverTrigger>
                          <PopoverContent marginStart={2}>
                            <PopoverArrow />
                            <PopoverBody>
                              <Text>
                                {t(
                                  'user_group_restricted_user',
                                  'Only media library restricted access users can be a part of user groups.',
                                )}
                              </Text>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      </HStack>
                      <IKReactSelect
                        menuPlacement="auto"
                        variant="compact"
                        placeholder={t('select_users', 'Select users')}
                        options={userOptions}
                        value={users}
                        onChange={users => {
                          setFieldValue('userField', users)
                          setUsers(users)
                        }}
                        isMulti
                        isClearable
                        menuPosition="fixed"
                        menuShouldScrollIntoView
                      />
                      {form.errors.userField ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.userField}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                    setUsers([])
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || loading || !dirty}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default CreateUpdateUserGroupModal
